import { useEffect, useState } from "react";
import { HttpError, request } from "../../../api/http";
import { useConfirm } from "../../../components/Dialog";
import { IconClose } from "../../../components/Icons";
import { useAuth } from "../../../context/AuthContext";
import {
  ResizeHandle,
  useResizableDrawer,
} from "../../../hooks/useResizableDrawer";
import {
  CreateManualTarificationPayload,
  ExistingManualTarification,
  ManualTarificationCoverage,
  MANUAL_TARIFICATION_COVERAGES,
  ManualTarificationTraitRow,
  UpdateManualTarificationPayload,
} from "../../../types/manualTarification";

const MIN_CAPITAL = 50000;
const MAX_CAPITAL = 500000;
// Backend domain floor (ManualTarificationCapital): a non-zero capital must be
// >= 10000. basic/premium use the stricter legacy UI bound (50k–500k); eg/multi
// per-trait capitals only need to clear this domain floor.
const MIN_TRAIT_CAPITAL = 10000;

type Props = {
  leadUlid: string;
  leadInternalId: number;
  defaultCapital: number | null;
  defaultCoverage: string | null;
  defaultEffectDate: string | null;
  onClose: () => void;
  onApplied: () => void;
};

type TraitFields = {
  surcharge: string;
  extracharge: string;
  capital: string;
  enabled: boolean;
};

const emptyTrait = (enabled: boolean): TraitFields => ({
  surcharge: "",
  extracharge: "",
  capital: "",
  enabled,
});

const today = (): string => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const normalizeCoverage = (raw: string | null): ManualTarificationCoverage => {
  if (raw === "basic" || raw === "eg" || raw === "multi") return raw;
  return "premium";
};

const num = (v: string): number => (v.trim() === "" ? 0 : Number(v));
const str = (n: number | undefined): string =>
  n === undefined || n === null ? "" : String(n);

// basic/premium use a single shared capital input (DEATH's, mirrored to
// DISABILITY for premium); eg/multi use per-trait capital fields.
const usesSharedCapital = (c: ManualTarificationCoverage): boolean =>
  c === "basic" || c === "premium";

export default function ManualTarificationDrawer({
  leadUlid,
  leadInternalId,
  defaultCapital,
  defaultCoverage,
  defaultEffectDate,
  onClose,
  onApplied,
}: Props) {
  const { token } = useAuth();
  const confirm = useConfirm();
  const { width, startResize } = useResizableDrawer({ initial: 560, min: 400 });

  const [loading, setLoading] = useState(true);
  // "view" = an existing tarification rendered read-only; "form" = editable
  // (creating a new one, or editing the existing after clicking "Editar").
  const [mode, setMode] = useState<"view" | "form">("form");
  const [existingUuid, setExistingUuid] = useState<string | null>(null);
  const [existing, setExisting] = useState<ExistingManualTarification | null>(
    null,
  );

  const [coverage, setCoverage] = useState<ManualTarificationCoverage>(
    normalizeCoverage(defaultCoverage),
  );
  const [death, setDeath] = useState<TraitFields>({
    ...emptyTrait(true),
    capital: defaultCapital !== null ? String(defaultCapital) : "",
  });
  const [disability, setDisability] = useState<TraitFields>(emptyTrait(false));
  const [seriousIllness, setSeriousIllness] = useState<TraitFields>(
    emptyTrait(false),
  );
  const [effectDate, setEffectDate] = useState(defaultEffectDate ?? today());

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    request<ExistingManualTarification>(
      `/api/admin/operations/leads/${leadUlid}/manual-tarification`,
      { token },
    )
      .then((res) => {
        if (cancelled || !res.exists) return;
        setExisting(res);
        setExistingUuid(res.tarificationUuid ?? null);
        setMode("view");
        prefillFromExisting(res);
      })
      .catch(() => {
        // Non-fatal: treat as "no tarification yet" and show a blank form.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadUlid, token]);

  const prefillFromExisting = (res: ExistingManualTarification) => {
    const cov = normalizeCoverage(res.coverage ?? null);
    setCoverage(cov);
    setEffectDate(res.tarifications?.effectDate ?? today());
    const byTrait = res.tarifications?.byTrait ?? {};
    const d = byTrait.DEATH;
    if (d) {
      setDeath({
        surcharge: str(d.surcharge),
        extracharge: str(d.extracharge),
        capital: str(d.capital),
        enabled: true,
      });
    }
    const di = byTrait.DISABILITY;
    setDisability({
      surcharge: str(di?.surcharge),
      extracharge: str(di?.extracharge),
      capital: str(di?.capital),
      enabled: !!di,
    });
    const si = byTrait.SERIOUS_ILLNESS;
    setSeriousIllness({
      surcharge: str(si?.surcharge),
      extracharge: str(si?.extracharge),
      capital: str(si?.capital),
      enabled: !!si,
    });
  };

  const buildTraitTarifications = (): ManualTarificationTraitRow[] => {
    const deathCap = num(death.capital);
    const rows: ManualTarificationTraitRow[] = [
      {
        trait: "DEATH",
        surcharge: num(death.surcharge),
        extracharge: num(death.extracharge),
        capital: deathCap,
      },
    ];
    if (coverage === "premium") {
      rows.push({
        trait: "DISABILITY",
        surcharge: num(disability.surcharge),
        extracharge: num(disability.extracharge),
        // premium requires the same capital as DEATH.
        capital: deathCap,
      });
    } else if (coverage === "eg") {
      rows.push({
        trait: "SERIOUS_ILLNESS",
        surcharge: num(seriousIllness.surcharge),
        extracharge: num(seriousIllness.extracharge),
        capital: num(seriousIllness.capital),
      });
    } else if (coverage === "multi") {
      if (disability.enabled) {
        rows.push({
          trait: "DISABILITY",
          surcharge: num(disability.surcharge),
          extracharge: num(disability.extracharge),
          capital: num(disability.capital),
        });
      }
      if (seriousIllness.enabled) {
        rows.push({
          trait: "SERIOUS_ILLNESS",
          surcharge: num(seriousIllness.surcharge),
          extracharge: num(seriousIllness.extracharge),
          capital: num(seriousIllness.capital),
        });
      }
    }
    return rows;
  };

  const validate = (): string | null => {
    const deathCap = num(death.capital);
    if (!(deathCap > 0)) {
      return "El capital de DEATH es obligatorio y debe ser mayor que 0.";
    }
    if (usesSharedCapital(coverage)) {
      if (deathCap < MIN_CAPITAL || deathCap > MAX_CAPITAL) {
        return `El capital debe estar entre ${MIN_CAPITAL.toLocaleString(
          "es-ES",
        )} y ${MAX_CAPITAL.toLocaleString("es-ES")}.`;
      }
    } else {
      // eg/multi: each emitted trait capital must clear the backend's 10k floor
      // (mirrors ManualTarificationCapital so the operator gets a friendly
      // message instead of a backend 400/500). Disabled traits aren't emitted.
      for (const row of buildTraitTarifications()) {
        if (!(row.capital >= MIN_TRAIT_CAPITAL)) {
          return `El capital de ${row.trait} debe ser de al menos ${MIN_TRAIT_CAPITAL.toLocaleString(
            "es-ES",
          )}.`;
        }
      }
    }
    if (!effectDate) return "La fecha de efecto es obligatoria.";
    return null;
  };

  const submit = async () => {
    if (submitting) return;
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    const isEdit = existingUuid !== null;
    const ok = await confirm({
      title: isEdit
        ? "Actualizar tarificación manual"
        : "Aplicar tarificación manual",
      message: isEdit
        ? "Se actualizará la tarificación manual de este lead con los valores indicados. ¿Continuar?"
        : "Se forzará la tarificación de este lead con los valores indicados. " +
          "El lead pasará a payment-method-pending y se recalculará el precio. ¿Continuar?",
      confirmLabel: isEdit ? "Actualizar" : "Aplicar tarificación",
      tone: "warning",
    });
    if (!ok) return;

    setSubmitting(true);
    setError(null);
    const traitTarifications = buildTraitTarifications();
    try {
      if (isEdit && existingUuid) {
        const body: UpdateManualTarificationPayload = {
          manualTarificationUuid: existingUuid,
          manualTarificationCoverage: coverage,
          traitTarifications,
          manualTarificationEffectDate: effectDate,
        };
        await request(
          `/api/admin/operations/leads/${leadUlid}/manual-tarification`,
          { method: "PATCH", token, body },
        );
      } else {
        const body: CreateManualTarificationPayload = {
          leadInternalId,
          manualTarificationCoverage: coverage,
          traitTarifications,
          manualTarificationEffectDate: effectDate,
        };
        await request(
          `/api/admin/operations/leads/${leadUlid}/manual-tarification`,
          { method: "POST", token, body },
        );
      }
      onApplied();
    } catch (err) {
      setError(
        err instanceof HttpError
          ? `HTTP ${err.status}: ${
              ((err.body as { message?: string })?.message ?? "").slice(0, 200) ||
              ((err.body as { error?: string })?.error ?? "")
            }`
          : "No se pudo aplicar la tarificación manual",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const showDisability = coverage === "premium" || coverage === "multi";
  const showSeriousIllness = coverage === "eg" || coverage === "multi";
  const disabilityToggleable = coverage === "multi";
  const seriousIllnessToggleable = coverage === "multi";

  return (
    <DrawerShell
      width={width}
      onStartResize={startResize}
      title="Tarificación manual"
      onClose={onClose}
    >
      {loading && (
        <div
          style={{ color: "#73879C", fontSize: ".85rem", marginBottom: "0.5rem" }}
        >
          Cargando…
        </div>
      )}

      {!loading && mode === "view" && existing && (
        <ExistingTarificationView
          existing={existing}
          onEdit={() => {
            setMode("form");
            setError(null);
          }}
        />
      )}

      {!loading && mode === "form" && (
        <>
          {existingUuid && (
            <div
              style={{
                background: "#fff3cd",
                color: "#856404",
                padding: "0.5rem 0.7rem",
                borderRadius: "0.4rem",
                fontSize: ".78rem",
                marginBottom: "0.85rem",
              }}
            >
              Editando la tarificación manual existente.
            </div>
          )}

          <Field label="Cobertura">
            <select
              value={coverage}
              onChange={(e) => {
                setCoverage(e.target.value as ManualTarificationCoverage);
                setError(null);
              }}
              style={selectStyle}
              disabled={submitting}
            >
              {MANUAL_TARIFICATION_COVERAGES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>

          <TraitSection
            title="Fallecimiento (DEATH)"
            required
            fields={death}
            onChange={setDeath}
            showCapital={!usesSharedCapital(coverage)}
            submitting={submitting}
          />

          {usesSharedCapital(coverage) && (
            <Field
              label={`Capital (${MIN_CAPITAL.toLocaleString(
                "es-ES",
              )}–${MAX_CAPITAL.toLocaleString("es-ES")})`}
            >
              <input
                type="number"
                value={death.capital}
                min={MIN_CAPITAL}
                max={MAX_CAPITAL}
                onChange={(e) =>
                  setDeath((p) => ({ ...p, capital: e.target.value }))
                }
                style={inputStyle}
                disabled={submitting}
              />
              {coverage === "premium" && (
                <span style={hintStyle}>
                  DISABILITY usa el mismo capital que DEATH.
                </span>
              )}
            </Field>
          )}

          {showDisability && (
            <TraitSection
              title="Invalidez (DISABILITY)"
              fields={disability}
              onChange={setDisability}
              showCapital={coverage === "multi"}
              toggleable={disabilityToggleable}
              submitting={submitting}
            />
          )}

          {showSeriousIllness && (
            <TraitSection
              title="Enfermedad grave (SERIOUS_ILLNESS)"
              fields={seriousIllness}
              onChange={setSeriousIllness}
              showCapital
              toggleable={seriousIllnessToggleable}
              submitting={submitting}
            />
          )}

          <hr
            style={{ border: 0, borderTop: "1px solid #eef0f2", margin: "0.9rem 0" }}
          />

          <Field label="Fecha de efecto">
            <input
              type="date"
              value={effectDate}
              min={today()}
              onChange={(e) => setEffectDate(e.target.value)}
              style={inputStyle}
              disabled={submitting}
            />
          </Field>

          {error && <ErrorBox>{error}</ErrorBox>}

          <DrawerFooter
            onClose={onClose}
            onSubmit={submit}
            submitting={submitting}
            submitLabel={
              existingUuid ? "Actualizar tarificación" : "Aplicar tarificación"
            }
          />
        </>
      )}
    </DrawerShell>
  );
}

function TraitSection({
  title,
  required,
  fields,
  onChange,
  showCapital,
  toggleable,
  submitting,
}: {
  title: string;
  required?: boolean;
  fields: TraitFields;
  onChange: React.Dispatch<React.SetStateAction<TraitFields>>;
  showCapital: boolean;
  toggleable?: boolean;
  submitting: boolean;
}) {
  const disabled = submitting || (toggleable ? !fields.enabled : false);
  return (
    <div
      style={{
        opacity: toggleable && !fields.enabled ? 0.55 : 1,
        marginBottom: "0.4rem",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          margin: "0.6rem 0 0.4rem",
        }}
      >
        <span
          style={{
            fontSize: ".72rem",
            fontWeight: 700,
            textTransform: "uppercase",
            color: "#0069d9",
            letterSpacing: ".4px",
          }}
        >
          {title}
        </span>
        {required && (
          <span
            style={{
              fontSize: ".62rem",
              color: "#b8860b",
              fontWeight: 700,
              textTransform: "uppercase",
            }}
          >
            requerido
          </span>
        )}
        {toggleable && (
          <label
            style={{
              marginLeft: "auto",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.3rem",
              fontSize: ".72rem",
              color: "#73879C",
            }}
          >
            <input
              type="checkbox"
              checked={fields.enabled}
              onChange={(e) =>
                onChange((p) => ({ ...p, enabled: e.target.checked }))
              }
              disabled={submitting}
            />
            Activa
          </label>
        )}
      </div>
      <Row>
        <Field label="Sobreprima %">
          <input
            type="number"
            value={fields.surcharge}
            onChange={(e) =>
              onChange((p) => ({ ...p, surcharge: e.target.value }))
            }
            style={inputStyle}
            disabled={disabled}
          />
        </Field>
        <Field label="Extraprima %">
          <input
            type="number"
            value={fields.extracharge}
            onChange={(e) =>
              onChange((p) => ({ ...p, extracharge: e.target.value }))
            }
            style={inputStyle}
            disabled={disabled}
          />
        </Field>
      </Row>
      {showCapital && (
        <Field label="Capital">
          <input
            type="number"
            value={fields.capital}
            min={0}
            onChange={(e) =>
              onChange((p) => ({ ...p, capital: e.target.value }))
            }
            style={inputStyle}
            disabled={disabled}
          />
        </Field>
      )}
    </div>
  );
}

function ExistingTarificationView({
  existing,
  onEdit,
}: {
  existing: ExistingManualTarification;
  onEdit: () => void;
}) {
  const byTrait = existing.tarifications?.byTrait ?? {};
  const traitOrder: Array<"DEATH" | "DISABILITY" | "SERIOUS_ILLNESS"> = [
    "DEATH",
    "DISABILITY",
    "SERIOUS_ILLNESS",
  ];
  return (
    <div>
      <div
        style={{
          background: "#fff3cd",
          color: "#856404",
          padding: "0.55rem 0.75rem",
          borderRadius: "0.4rem",
          fontSize: ".8rem",
          marginBottom: "0.9rem",
        }}
      >
        Este lead ya tiene una tarificación manual aplicada.
      </div>
      <ReadField label="Cobertura" value={existing.coverage ?? "—"} />
      <ReadField label="Downgraded" value={existing.isDowngraded ? "Sí" : "No"} />
      <ReadField
        label="Fecha de efecto"
        value={existing.tarifications?.effectDate ?? "—"}
      />
      {traitOrder.map((t) => {
        const row = byTrait[t];
        if (!row) return null;
        return (
          <div key={t} style={{ marginTop: "0.6rem" }}>
            <div
              style={{
                fontSize: ".72rem",
                fontWeight: 700,
                textTransform: "uppercase",
                color: "#0069d9",
                marginBottom: "0.2rem",
              }}
            >
              {t}
            </div>
            <ReadField label="Sobreprima %" value={row.surcharge ?? "—"} />
            <ReadField label="Extraprima %" value={row.extracharge ?? "—"} />
            <ReadField
              label="Capital"
              value={
                row.capital !== undefined
                  ? row.capital.toLocaleString("es-ES")
                  : "—"
              }
            />
          </div>
        );
      })}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
        <button type="button" onClick={onEdit} style={primaryBtn}>
          Editar
        </button>
      </div>
    </div>
  );
}

function ReadField({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        padding: "0.3rem 0",
        borderBottom: "1px solid #f4f5f7",
        fontSize: ".85rem",
      }}
    >
      <span style={{ color: "#73879C" }}>{label}</span>
      <span style={{ color: "#1f2933", fontWeight: 600 }}>{value}</span>
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem" }}
    >
      {children}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "grid", gap: "0.3rem", marginBottom: "0.85rem" }}>
      <span
        style={{
          fontSize: ".72rem",
          color: "#73879C",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: ".3px",
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function DrawerShell({
  title,
  onClose,
  children,
  width,
  onStartResize,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  width: number;
  onStartResize: (event: React.MouseEvent) => void;
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        zIndex: 50,
        display: "flex",
        justifyContent: "flex-end",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width,
          height: "100%",
          background: "white",
          boxShadow: "-2px 0 16px rgba(0,0,0,.18)",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          whiteSpace: "normal",
          wordBreak: "break-word",
          textAlign: "left",
        }}
      >
        <ResizeHandle onMouseDown={onStartResize} />
        <header
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0.95rem 1.1rem",
            borderBottom: "1px solid #e9ecef",
            gap: "0.75rem",
          }}
        >
          <h2 style={{ margin: 0, fontSize: "1rem", color: "#1f2933", flex: 1 }}>
            {title}
          </h2>
          <button type="button" onClick={onClose} style={closeBtn}>
            <IconClose />
          </button>
        </header>
        <div style={{ flex: 1, overflow: "auto", padding: "1rem 1.1rem" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function DrawerFooter({
  onClose,
  onSubmit,
  submitting,
  submitLabel,
}: {
  onClose: () => void;
  onSubmit: () => void;
  submitting: boolean;
  submitLabel: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        gap: "0.5rem",
        justifyContent: "flex-end",
        marginTop: "1.1rem",
      }}
    >
      <button
        type="button"
        onClick={onClose}
        style={ghostBtn}
        disabled={submitting}
      >
        Cancelar
      </button>
      <button
        type="button"
        onClick={onSubmit}
        style={primaryBtn}
        disabled={submitting}
      >
        {submitting ? "Aplicando…" : submitLabel}
      </button>
    </div>
  );
}

function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: "0.7rem",
        padding: "0.5rem 0.7rem",
        borderRadius: "0.4rem",
        background: "#fde2e2",
        color: "#b32424",
        fontSize: ".8rem",
      }}
    >
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.45rem 0.6rem",
  fontSize: ".88rem",
  border: "1px solid #dee2e6",
  borderRadius: "0.4rem",
  background: "#f8f9fb",
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  appearance: "auto",
};

const hintStyle: React.CSSProperties = {
  fontSize: ".7rem",
  color: "#73879C",
  marginTop: "0.15rem",
};

const closeBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "2rem",
  height: "2rem",
  padding: 0,
  borderRadius: "0.4rem",
  background: "#f8f9fb",
  color: "#1f2933",
  border: "1px solid #dee2e6",
  cursor: "pointer",
};

const ghostBtn: React.CSSProperties = {
  padding: "0.45rem 0.95rem",
  borderRadius: "0.4rem",
  background: "white",
  color: "#73879C",
  border: "1px solid #dee2e6",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};

const primaryBtn: React.CSSProperties = {
  padding: "0.45rem 1.1rem",
  borderRadius: "0.4rem",
  background: "#0069d9",
  color: "white",
  border: "1px solid #0069d9",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};
